// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import yaml from 'js-yaml';
import { v4 as uuidv4 } from 'uuid';

import validatePipelineConfig from './validatePipelineConfig';

function buildClustersMaps(clusters) {
  const byName = {};
  const byId = {};
  const byUrl = {};

  clusters.forEach(cluster => {
    byName[cluster.name] = cluster;
    byId[cluster.id] = cluster;

    if (cluster.url) {
      if (!byUrl[cluster.url]) {
        byUrl[cluster.url] = [];
      }
      byUrl[cluster.url].push(cluster);
    }
  });

  return { byName, byId, byUrl };
}

export default async function processBulkCreate({ fileContent, clusters, existingPipelines, bulkInsertPipelines }) {
  const createdPipelines = [];
  const failedPipelines = [];
  const validPipelines = [];

  const parsed = yaml.load(fileContent);
  const pipelinesData = parsed.pipelines;

  const clustersMap = buildClustersMaps(clusters);

  // Validate all pipelines first
  for (const [yamlKey, config] of Object.entries(pipelinesData)) {
    const validation = validatePipelineConfig({
      config,
      existingPipelines,
      clustersMap,
      yamlKey,
    });

    if (!validation.valid) {
      failedPipelines.push({
        writekey: config.writekey,
        name: config.name,
        clusterName: config.clusterName,
        clusterId: config.clusterId,
        clusterUrl: config.clusterUrl,
        errors: validation.errors.map(error => ({
          error: 'Validation Error',
          reason: error,
        })),
      });
    } else {
      const pipelineId = uuidv4();
      validPipelines.push({
        id: pipelineId,
        name: config.name,
        writekey: config.writekey,
        clusterId: validation.clusterId,
        originalConfig: config,
      });

      // Add to existingPipelines for subsequent validations
      existingPipelines.push({
        id: pipelineId,
        name: config.name,
        writekey: config.writekey,
        clusterId: validation.clusterId,
      });
    }
  }

  // Insert valid pipelines in chunks of 100
  const CHUNK_SIZE = 100;
  for (let i = 0; i < validPipelines.length; i += CHUNK_SIZE) {
    const chunk = validPipelines.slice(i, i + CHUNK_SIZE);
    const pipelinesToInsert = chunk.map(({ id, name, writekey, clusterId }) => ({
      id,
      name,
      writekey,
      clusterId,
    }));

    try {
      const result = await bulkInsertPipelines(pipelinesToInsert);

      if (result?.data?.insert_pipelines?.returning) {
        const insertedPipelines = result.data.insert_pipelines.returning;
        insertedPipelines.forEach(pipeline => {
          const originalConfig = chunk.find(p => p.id === pipeline.id)?.originalConfig;
          createdPipelines.push({
            id: pipeline.id,
            name: pipeline.name,
            writekey: pipeline.writekey,
            clusterName: originalConfig?.clusterName,
            clusterId: originalConfig?.clusterId,
            clusterUrl: originalConfig?.clusterUrl,
            status: 'created',
            createdAt: pipeline.createdAt,
          });
        });
      }
    } catch (error) {
      chunk.forEach(({ originalConfig }) => {
        failedPipelines.push({
          writekey: originalConfig.writekey,
          name: originalConfig.name,
          clusterName: originalConfig.clusterName,
          clusterId: originalConfig.clusterId,
          clusterUrl: originalConfig.clusterUrl,
          errors: [
            {
              error: error.name || 'Insert Error',
              reason: error.message || 'Failed to create pipeline',
            },
          ],
        });
      });
    }
  }

  return {
    createdPipelines,
    failedPipelines,
    succeeded: createdPipelines.length,
    failed: failedPipelines.length,
  };
}
